import React from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { isDepartmentManager } from '../utils/roleHelpers';
import UploadProgressIndicator from './UploadProgressIndicator';
import TopBar from './TopBar';
import TrialBanner from './TrialBanner';
import { useModules } from '../modules/useModules';
import SpocoDocLogo from './SpocoDocLogo';
import ChatModal from './ChatModal';
import { approvalsAPI, helpAPI } from '../api/client';
import VoiceAgentButton from './VoiceAgentButton';
import apiClient from '../api/client';
import { 
  LayoutDashboard, 
  Building2, 
  Settings, 
  Shield, 
  FileText, 
  FilePlus, 
  CheckSquare, 
  Users, 
  FolderKanban, 
  DollarSign, 
  BarChart3, 
  UserCog, 
  Lock, 
  Activity, 
  Upload,
  Briefcase,
  Layers,
  Globe,
  Coins,
  RefreshCw,
  Receipt,
  Car,
  ClipboardList,
  Archive,
  PieChart,
  Calendar,
  Send,
  Link2,
  BookOpen,
  Clock,
  Mail,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  PiggyBank,
  ChartNoAxesCombined,
  Puzzle,
  ChartCandlestick,
  Handshake,
  Wallet,
  Bell,
  MessageCircle,
  TrendingUp,
  ShoppingCart,
  KeyRound,
  Search,
  PanelLeft,
  PanelLeftClose,
  Bot,
  Mic,
  Phone,
  LogOut,
  User
} from 'lucide-react';

export default function Layout() {
  const { user, logout, currentCompanyId, isSuperAdmin } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const { isEnabled: isModuleEnabled } = useModules();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [expandedGroups, setExpandedGroups] = React.useState(['actionItems', 'finance', 'myExpenses', 'main', 'monitoring', 'dashboard', 'admin', 'budgets', 'reports']);
  const [pendingCounts, setPendingCounts] = React.useState({ invoiceCount: 0, expenseCount: 0, totalCount: 0, companyInvoiceCount: 0, companyExpenseCount: 0, companyTotalCount: 0, rateReconciliationCount: 0, vendorApprovalCount: 0, postToFinanceCount: 0, openTicketCount: 0 });
  const [chatOpen, setChatOpen] = React.useState(false);
  const [menuMode, setMenuMode] = React.useState('standard');
  const [compactExpanded, setCompactExpanded] = React.useState(false);
  const [hoveredGroup, setHoveredGroup] = React.useState(null);
  const [flyoutPos, setFlyoutPos] = React.useState(0);
  const [menuSearch, setMenuSearch] = React.useState('');
  const [compactSearchOpen, setCompactSearchOpen] = React.useState(false);
  const hoverTimeoutRef = React.useRef(null);
  const [showMobileProfile, setShowMobileProfile] = React.useState(false);
  const mobileProfileRef = React.useRef(null);

  // Fetch pending task counts for sidebar badges
  React.useEffect(() => {
    let mounted = true;
    const fetchCounts = async () => {
      try {
        const res = await approvalsAPI.getPendingCount();
        if (mounted) setPendingCounts(prev => ({ ...prev, ...res.data }));
      } catch { /* ignore */ }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => { mounted = false; clearInterval(interval); };
  }, [currentCompanyId]);

  // Poll open ticket count for platform admins (every 60s)
  React.useEffect(() => {
    if (!user?.isSuperAdmin) return;
    let mounted = true;
    const fetchTicketCount = async () => {
      try {
        const res = await helpAPI.getOpenCount();
        if (mounted) setPendingCounts(prev => ({ ...prev, openTicketCount: res.data.count || 0 }));
      } catch { /* ignore */ }
    };
    fetchTicketCount();
    const interval = setInterval(fetchTicketCount, 60000);
    return () => { mounted = false; clearInterval(interval); };
  }, [user?.isSuperAdmin]);

  // Load menu mode preference — DB (via user profile) takes priority over localStorage
  React.useEffect(() => {
    if (!user?.email) return;
    // Apply from user profile if available (cross-browser source of truth)
    const prefs = user?.uiPreferences || {};
    if (prefs.menuMode === 'standard' || prefs.menuMode === 'compact') {
      setMenuMode(prefs.menuMode);
      localStorage.setItem(`menuMode_${user.email}`, prefs.menuMode);
    } else {
      // Fall back to localStorage for instant load before profile refresh
      const stored = localStorage.getItem(`menuMode_${user.email}`);
      if (stored === 'standard' || stored === 'compact') setMenuMode(stored);
    }
    if (typeof prefs.menuCompactExpanded === 'boolean') {
      setCompactExpanded(prefs.menuCompactExpanded);
      localStorage.setItem(`menuCompactExpanded_${user.email}`, String(prefs.menuCompactExpanded));
    } else {
      const storedExp = localStorage.getItem(`menuCompactExpanded_${user.email}`);
      if (storedExp === 'true') setCompactExpanded(true);
    }
    // Restore expanded groups from localStorage
    try {
      const storedGroups = localStorage.getItem(`expandedGroups_${user.email}`);
      if (storedGroups) {
        const parsed = JSON.parse(storedGroups);
        if (Array.isArray(parsed)) setExpandedGroups(parsed);
      }
    } catch { /* ignore */ }
  }, [user?.email, user?.uiPreferences]);

  // Clean up hover timeout on unmount
  React.useEffect(() => {
    return () => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Close mobile profile dropdown on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (mobileProfileRef.current && !mobileProfileRef.current.contains(e.target)) {
        setShowMobileProfile(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => {
      const next = prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId];
      if (user?.email) localStorage.setItem(`expandedGroups_${user.email}`, JSON.stringify(next));
      return next;
    });
  };

  const toggleMenuMode = () => {
    const next = menuMode === 'standard' ? 'compact' : 'standard';
    setMenuMode(next);
    if (user?.email) localStorage.setItem(`menuMode_${user.email}`, next);
    setHoveredGroup(null);
    setCompactSearchOpen(false);
    setMenuSearch('');
    // Persist to DB via UserPreferences table (fire-and-forget)
    apiClient.put('/settings/preferences/menuMode', { value: next }).catch(() => {});
  };

  const toggleCompactExpanded = () => {
    const next = !compactExpanded;
    setCompactExpanded(next);
    if (user?.email) localStorage.setItem(`menuCompactExpanded_${user.email}`, String(next));
    setHoveredGroup(null);
    setCompactSearchOpen(false);
    setMenuSearch('');
    // Persist to DB via UserPreferences table (fire-and-forget)
    apiClient.put('/settings/preferences/menuCompactExpanded', { value: next }).catch(() => {});
  };

  const handleGroupHoverEnter = (groupId, e) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyoutPos(rect.top);
    setHoveredGroup(groupId);
  };

  const handleGroupHoverLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setHoveredGroup(null), 80);
  };

  const handleFlyoutHoverEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  };

  const handleFlyoutHoverLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setHoveredGroup(null), 80);
  };

  // Platform Owner Menu
  const platformMenuGroups = [
    {
      id: 'main',
      label: 'Platform',
      sectionIcon: LayoutDashboard,
      items: [
        { path: '/', label: 'Platform Dashboard', permission: null, icon: LayoutDashboard },
        { path: '/companies', label: 'Companies', permission: null, icon: Building2 },
        { path: '/processesmap', label: 'Processes Map', permission: null, icon: Layers },
        { path: '/users', label: 'Platform Users', permission: null, icon: Users },
        { path: '/settings', label: 'Platform Settings', permission: null, icon: Settings },
      ]
    },
    {
      id: 'monitoring',
      label: 'Monitoring',
      sectionIcon: Activity,
      items: [
        { path: '/audit-logs', label: 'Audit Logs (All Companies)', permission: null, icon: Shield },
        { path: '/license-status', label: 'Subscriptions & Usage', permission: null, icon: UserCog },
        { path: '/platform/resources', label: 'OCR Pool & Storage', permission: null, icon: Settings },
        { path: '/platform/teams-usage', label: 'Teams Usage', permission: null, icon: Bot },
        { path: '/platform/voice-usage', label: 'Voice Usage & Quotas', permission: null, icon: Phone },
        { path: '/support/tickets', label: 'Support Tickets', permission: null, icon: MessageCircle },
      ]
    },
  ];

  // ──────────────────────────────────────────────────────────────────────────
  // Unified Company Menu
  // Single menu for ALL company-side roles (Finance Admin, Department Manager,
  // Department Approver, Employee, Finance User, Viewer, and any CUSTOM role
  // created via Role Permissions).
  //
  // Layout matches the Finance Admin sidebar so every role sees the same group
  // structure (Action Items → Finance → Budgets & Projects → Reports →
  // My Expenses → Administration → Settings). Items are gated purely by
  // permissions, so roles with fewer permissions just see fewer items, and
  // empty groups auto-hide. New custom roles inherit this menu automatically —
  // simply assign the relevant permissions and the matching items appear.
  // ──────────────────────────────────────────────────────────────────────────
  const unifiedCompanyMenuGroups = [
    {
      id: 'actionItems',
      label: 'Action Items',
      sectionIcon: CheckSquare,
      items: [
        { path: '/approvals', label: 'Invoice Approvals', permission: 'approve_invoices', icon: CheckSquare },
        { path: '/expense-approvals', label: 'Expense Approvals', permission: 'approve_expenses', icon: CheckSquare },
        { path: '/exchange-rate-reconciliation', label: 'Rate Reconciliation', permission: 'manage_finance_settings', icon: ChartCandlestick },
        { path: '/vendors', label: 'Vendors', permission: 'manage_vendors', icon: Handshake },
        { path: '/invoice-posting', label: 'Post to Finance', permission: 'manage_finance_settings', icon: Send },
      ]
    },
    {
      id: 'finance',
      label: 'Finance',
      sectionIcon: DollarSign,
      items: [
        { path: '/invoices', label: 'Invoices', permission: 'view_invoices', icon: FileText },
        { path: '/accruals', label: 'Accruals', permission: 'manage_finance_settings', icon: BookOpen },
        { path: '/subscriptions', label: 'Subscriptions', permission: 'manage_finance_settings', showForDeptManager: true, icon: RefreshCw },
        { path: '/expenses?view=all', label: 'All Expenses', permission: 'view_all_expenses', icon: ClipboardList },
        { path: '/purchase-orders', label: 'Purchase Orders', permissions: ['manage_finance_settings', 'view_purchase_orders', 'manage_purchase_orders', 'create_purchase_orders'], module: 'purchase_orders', showForDeptManager: true, icon: ShoppingCart },
      ]
    },
    {
      id: 'budgets',
      label: 'Budgets & Projects',
      sectionIcon: PiggyBank,
      items: [
        { path: '/budget-analytics', label: 'Budget Analytics', permission: 'view_budgets', icon: ChartNoAxesCombined },
        { path: '/budget-headers', label: 'Budgets', permission: 'manage_budgets', showForDeptManager: true, icon: PiggyBank },
        { path: '/projects', label: 'Projects', permission: 'manage_finance_settings', module: 'projects', icon: Puzzle},
      ]
    },
    {
      id: 'reports',
      label: 'Reports',
      sectionIcon: BarChart3,
      items: [
        { path: '/finance-dashboard', label: 'Finance Dashboard', permission: 'view_finance_reports', icon: PieChart },
        { path: '/spend-analytics', label: 'Spend Intelligence', permission: 'view_finance_reports', icon: TrendingUp },
        { path: '/reports', label: 'Reports', permission: 'view_reports', icon: BarChart3 },
        { path: '/vat-claims', label: 'VAT Claims', permission: 'view_finance_reports', icon: Receipt },
      ]
    },
    {
      id: 'myExpenses',
      label: 'My Expenses',
      sectionIcon: Wallet,
      items: [
        { path: '/expenses', label: 'My Expenses', permission: 'view_my_expenses', icon: Wallet },
        { path: '/expenses/submit', label: 'Submit Expense', permission: 'submit_expenses', icon: FilePlus },
      ]
    },
    {
      id: 'aiAgent',
      label: 'AI Agent',
      sectionIcon: Bot,
      items: [
        { path: '/voice-agent-guide', label: 'Voice Agent Guide', icon: Mic },
        { path: '/ai-standup', label: 'AI Standup', module: 'teams', icon: Bot },
        { path: '/voice-call-logs', label: 'Voice Call Logs', permission: 'manage_finance_settings', icon: Phone },
        { path: '/voice-queries', label: 'Requested Queries Approval', permission: 'manage_finance_settings', icon: CheckSquare },
      ]
    },
    {
      id: 'admin',
      label: 'Administration',
      sectionIcon: Shield,
      items: [
        { path: '/admin', label: 'Dashboard', permission: 'manage_users', icon: LayoutDashboard },
        { path: '/users', label: 'Users', permission: 'manage_users', icon: Users },
        { path: '/departments', label: 'Departments', permission: 'manage_departments', icon: FolderKanban },
        { path: '/user-departments', label: 'User Departments', permission: 'manage_users', icon: UserCog },
        { path: '/role-permissions', label: 'Role Permissions', permission: 'manage_roles', icon: Lock },
        { path: '/audit-logs', label: 'Audit Logs', permission: 'view_audit_logs', icon: Shield },
        { path: '/user-activity', label: 'User Activity', permission: 'manage_users', icon: Activity },
        { path: '/activity-feed', label: 'Activity Feed', permission: 'view_audit_logs', icon: Activity },
        { path: '/license-status', label: 'License Status', permission: 'manage_users', icon: UserCog },
        { path: '/scheduler', label: 'Scheduler & Automation', permission: 'manage_finance_settings', module: 'scheduler', icon: Clock },
        { path: '/teams-settings', label: 'Teams Settings', permission: 'manage_users', module: 'teams', icon: Bot },
        { path: '/company-settings', label: 'Company Settings', permission: 'manage_finance_settings', icon: Settings },
        { path: '/api-tokens', label: 'API Tokens', permission: 'view_api_tokens', icon: KeyRound },
      ]
    },
  ];

  // Choose menu: platform owners get the platform menu; everyone else gets the
  // unified company menu (filtered by permissions). This keeps the same
  // formatting for all current roles AND any new custom role.
  const menuGroups = user?.isSuperAdmin ? platformMenuGroups : unifiedCompanyMenuGroups;

  const shouldShowNavItem = (item) => {
    // Platform owners see everything
    if (user?.isSuperAdmin) return true;
    // Check superAdminOnly flag first
    if (item.superAdminOnly && !isSuperAdmin) return false;
    // Hide items whose module is not enabled (module gate)
    if (item.module && !isModuleEnabled(item.module)) return false;
    // Department Manager role gate (in addition to permission gate)
    if (item.showForDeptManager && isDepartmentManager(user)) return true;
    // Always show items with no permission requirement
    if (!item.permission && !item.permissions) return true;
    // SuperAdmin sees everything
    if (isSuperAdmin) return true;
    // While loading, hide permission-gated items to prevent flash
    if (permissionsLoading) return false;
    // Support array of permissions (OR logic)
    if (item.permissions) return item.permissions.some(p => hasPermission(p));
    // Otherwise check single permission
    return hasPermission(item.permission);
  };

  const currentCompany = user?.companies?.find(c => c.CompanyID === currentCompanyId);

  return (
    <div className="h-screen bg-gray-100 flex overflow-hidden">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 bg-white dark:bg-slate-900 transform transition-all duration-200 ease-in-out
        lg:translate-x-0 lg:static lg:inset-0
        ${menuMode === 'compact' ? (compactExpanded ? 'w-44' : 'w-14') : 'w-64'}
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-screen">

          {/* ── Logo bar ── */}
          <div className="flex items-center h-12 bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
            {menuMode === 'compact' && !compactExpanded ? (
              // Collapsed compact: full bar is a click target to expand
              <button
                onClick={toggleCompactExpanded}
                className="flex-1 flex items-center justify-between h-full px-1 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
                title="Expand sidebar"
              >
                <span className="flex-1 flex items-center justify-center">
                  <SpocoDocLogo size={26} showText={false} />
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              </button>
            ) : (
              // Standard or compact-expanded: logo + text + optional chevron
              <div className="flex items-center gap-2 flex-1 px-3 min-w-0">
                <SpocoDocLogo
                  size={28}
                  showText={true}
                  textClass="text-base text-gray-900 dark:text-white truncate"
                />
                {menuMode === 'compact' && (
                  <button
                    onClick={toggleCompactExpanded}
                    className="flex-shrink-0 ml-auto p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
                    title="Collapse sidebar"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          {menuMode === 'compact' ? (
            <>
              {/* Inline search bar – compact-expanded only */}
              {compactExpanded && (
                <div className="px-3 pt-3 pb-1 flex-shrink-0">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={menuSearch}
                      onChange={e => setMenuSearch(e.target.value)}
                      placeholder="Filter menu…"
                      className="w-full pl-7 pr-3 py-1.5 text-xs bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md text-gray-700 dark:text-slate-300 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-cyan-500"
                    />
                    {menuSearch && (
                      <button onClick={() => setMenuSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">×</button>
                    )}
                  </div>
                </div>
              )}
              {/* ── Compact navigation – section icons ── */}
              <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
              {/* Search icon – collapsed compact only */}
              {!compactExpanded && (
                <button
                  onClick={() => { setCompactSearchOpen(o => !o); setMenuSearch(''); }}
                  className={`w-full flex justify-center p-3 rounded-lg mb-1 transition-colors ${
                    compactSearchOpen
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800/60 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  title="Search menu"
                >
                  <Search className="w-5 h-5" />
                </button>
              )}
              {menuGroups.map((group) => {
                const visibleItems = group.items.filter(shouldShowNavItem);
                // In compact-expanded with active search, hide sections with no matching items
                const hasMatch = !menuSearch.trim() || visibleItems.some(i => i.label.toLowerCase().includes(menuSearch.toLowerCase()));
                if (visibleItems.length === 0 || (compactExpanded && !hasMatch)) return null;
                const SectionIcon = group.sectionIcon;
                const hasActiveItem = visibleItems.some(item => {
                  const [ip, iq] = item.path.split('?');
                  if (iq) return location.pathname === ip && location.search === `?${iq}`;
                  return location.pathname === ip;
                });
                let sectionBadge = 0;
                if (group.id === 'actionItems') {
                  visibleItems.forEach(i => {
                    if (i.path === '/approvals') sectionBadge += pendingCounts.companyInvoiceCount || 0;
                    else if (i.path === '/expense-approvals') sectionBadge += (pendingCounts.companyExpenseCount || pendingCounts.expenseCount || 0);
                    else if (i.path === '/exchange-rate-reconciliation') sectionBadge += pendingCounts.rateReconciliationCount || 0;
                    else if (i.path === '/invoice-posting') sectionBadge += pendingCounts.postToFinanceCount || 0;
                    else if (i.path === '/vendors') sectionBadge += pendingCounts.vendorApprovalCount || 0;
                  });
                }
                return (
                  <div
                    key={group.id}
                    className="relative"
                    onMouseEnter={(e) => handleGroupHoverEnter(group.id, e)}
                    onMouseLeave={handleGroupHoverLeave}
                  >
                    <button
                      className={`w-full flex items-center relative rounded-lg transition-colors ${
                        compactExpanded ? 'px-3 py-2.5 gap-2.5' : 'justify-center p-3'
                      } ${
                        hasActiveItem
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800/60 hover:text-gray-900 dark:hover:text-white'
                      }`}
                      title={compactExpanded ? undefined : group.label}
                    >
                      {SectionIcon && <SectionIcon className="w-5 h-5 flex-shrink-0" />}
                      {compactExpanded && (
                        <span className="text-[11px] font-bold uppercase tracking-widest truncate flex-1 text-left">
                          {group.label}
                        </span>
                      )}
                      {sectionBadge > 0 && (
                        <span className={`${compactExpanded ? 'ml-auto' : 'absolute top-1 right-1'} min-w-[14px] h-3.5 px-0.5 text-[9px] font-bold rounded-full bg-red-500 text-white flex items-center justify-center`}>
                          {sectionBadge > 9 ? '9+' : sectionBadge}
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
              </nav>
            </>
          ) : (
            /* ── Standard navigation ── */
            <>
              {/* Search bar */}
              <div className="px-3 pt-3 pb-1 flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={menuSearch}
                    onChange={e => setMenuSearch(e.target.value)}
                    placeholder="Filter menu…"
                    className="w-full pl-7 pr-3 py-1.5 text-xs bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md text-gray-700 dark:text-slate-300 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-cyan-500"
                  />
                  {menuSearch && (
                    <button
                      onClick={() => setMenuSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >×</button>
                  )}
                </div>
              </div>

              <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
                {menuGroups.map((group) => {
                  const allVisible = group.items.filter(shouldShowNavItem);
                  // Apply search filter
                  const visibleItems = menuSearch.trim()
                    ? allVisible.filter(i => i.label.toLowerCase().includes(menuSearch.toLowerCase()))
                    : allVisible;
                  if (visibleItems.length === 0) return null;

                  const isExpanded = menuSearch.trim() ? true : expandedGroups.includes(group.id);

                  return (
                    <div key={group.id} className="mb-2">
                      {/* Group Header */}
                      <button
                        onClick={() => { if (!menuSearch.trim()) toggleGroup(group.id); }}
                        className="w-full flex items-center justify-between px-3 py-2.5 text-[11px] font-bold text-blue-600 dark:text-cyan-400 uppercase tracking-widest hover:text-blue-500 dark:hover:text-cyan-300 transition-colors border-b border-gray-200 dark:border-slate-700/60 mb-1"
                      >
                        <span className="flex items-center gap-2 text-left">
                          {group.label}
                          {(group.id === 'actionItems') && (() => {
                            let count = 0;
                            allVisible.forEach(i => {
                              if (i.path === '/approvals') count += pendingCounts.companyInvoiceCount;
                              else if (i.path === '/department-approvals') count += pendingCounts.invoiceCount;
                              else if (i.path === '/expense-approvals') count += (pendingCounts.companyExpenseCount || pendingCounts.expenseCount);
                              else if (i.path === '/exchange-rate-reconciliation') count += pendingCounts.rateReconciliationCount;
                              else if (i.path === '/vendors' && group.id === 'myTasks') count += pendingCounts.vendorApprovalCount;
                              else if (i.path === '/invoice-posting') count += pendingCounts.postToFinanceCount;
                            });
                            if (count <= 0) return null;
                            return (
                              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[14px] font-medium rounded-full bg-red-500 text-white">
                                {count > 9 ? '9+' : count}
                              </span>
                            );
                          })()}
                        </span>
                        {!menuSearch.trim() && (isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
                      </button>

                      {/* Group Items */}
                      {isExpanded && (
                        <div className="space-y-1 mt-1">
                          {visibleItems.map((item) => {
                            const [itemPath, itemQuery] = item.path.split('?');
                            let isActive;
                            if (itemQuery) {
                              isActive = location.pathname === itemPath && location.search === `?${itemQuery}`;
                            } else if (itemPath === '/help') {
                              isActive = (location.pathname === '/help' || location.pathname.startsWith('/help/'))
                                && !location.pathname.startsWith('/help/manage');
                            } else if (itemPath === '/help/manage') {
                              isActive = location.pathname === '/help/manage' || location.pathname.startsWith('/help/manage/');
                            } else if (itemPath === '/support/tickets') {
                              isActive = location.pathname.startsWith('/support/tickets');
                            } else if (itemPath === '/support/new') {
                              isActive = location.pathname === '/support/new';
                            } else {
                              isActive = location.pathname === itemPath && !location.search;
                            }
                            const IconComponent = item.icon;
                            const badgeCount =
                              item.path === '/approvals' ? pendingCounts.companyInvoiceCount
                              : item.path === '/department-approvals' ? pendingCounts.invoiceCount
                              : item.path === '/expense-approvals' ? (pendingCounts.companyExpenseCount || pendingCounts.expenseCount)
                              : item.path === '/exchange-rate-reconciliation' ? pendingCounts.rateReconciliationCount
                              : (item.path === '/vendors' && group.id === 'actionItems') ? pendingCounts.vendorApprovalCount
                              : item.path === '/invoice-posting' ? pendingCounts.postToFinanceCount
                              : item.path === '/support/tickets' ? pendingCounts.openTicketCount
                              : 0;
                            return (
                              <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => { setSidebarOpen(false); setMenuSearch(''); }}
                                className={`
                                  flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                                  ${isActive
                                    ? 'bg-primary-600 text-white'
                                    : 'text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800/60 hover:text-gray-900 dark:hover:text-white'
                                  }
                                `}
                              >
                                <IconComponent className="w-5 h-5 mr-3 flex-shrink-0" />
                                <span className="flex-1">{item.label}</span>
                                {badgeCount > 0 && (
                                  <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[14px] font-bold rounded-full bg-red-500 text-white">
                                    {badgeCount}
                                  </span>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </>
          )}

          {/* ── Sidebar footer: compact/standard toggle ── */}
          <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 px-2 py-2">
            <button
              onClick={toggleMenuMode}
              className={`w-full flex items-center rounded-lg px-2 py-2 text-xs font-medium text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white transition-colors gap-2 ${
                menuMode === 'compact' && !compactExpanded ? 'justify-center' : ''
              }`}
              title={menuMode === 'compact' ? 'Switch to Standard Menu' : 'Switch to Compact Menu'}
            >
              {menuMode === 'compact'
                ? <PanelLeft className="w-4 h-4 flex-shrink-0" />
                : <PanelLeftClose className="w-4 h-4 flex-shrink-0" />
              }
              {(menuMode === 'standard' || compactExpanded) && (
                <span className="truncate">
                  {menuMode === 'compact' ? 'Standard Menu' : 'Compact Menu'}
                </span>
              )}
            </button>
          </div>

        </div>
      </aside>

      {/* Compact mode flyout panel – rendered as a fixed overlay so it escapes the sidebar overflow */}
      {menuMode === 'compact' && hoveredGroup && !compactSearchOpen && (() => {
        const flyGroup = menuGroups.find(g => g.id === hoveredGroup);
        if (!flyGroup) return null;
        const flyItems = flyGroup.items.filter(shouldShowNavItem).filter(i =>
          !menuSearch.trim() || i.label.toLowerCase().includes(menuSearch.toLowerCase())
        );
        if (flyItems.length === 0) return null;
        return (
          <div
            className="fixed bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 z-[60] overflow-hidden"
            style={{ left: compactExpanded ? 176 : 56, top: flyoutPos, width: 224, maxHeight: `calc(100vh - ${flyoutPos}px - 16px)` }}
            onMouseEnter={handleFlyoutHoverEnter}
            onMouseLeave={handleFlyoutHoverLeave}
          >
            <div className="px-3 py-2.5 border-b border-gray-100 dark:border-slate-700/60 bg-gray-50 dark:bg-slate-800">
              <p className="text-[11px] font-bold text-blue-600 dark:text-cyan-400 uppercase tracking-widest">{flyGroup.label}</p>
            </div>
            <div className="py-1 overflow-y-auto" style={{ maxHeight: '60vh' }}>
              {flyItems.map((item) => {
                const [itemPath, itemQuery] = item.path.split('?');
                let isActive;
                if (itemQuery) {
                  isActive = location.pathname === itemPath && location.search === `?${itemQuery}`;
                } else if (itemPath === '/help') {
                  isActive = (location.pathname === '/help' || location.pathname.startsWith('/help/'))
                    && !location.pathname.startsWith('/help/manage');
                } else if (itemPath === '/help/manage') {
                  isActive = location.pathname === '/help/manage' || location.pathname.startsWith('/help/manage/');
                } else if (itemPath === '/support/tickets') {
                  isActive = location.pathname.startsWith('/support/tickets');
                } else if (itemPath === '/support/new') {
                  isActive = location.pathname === '/support/new';
                } else {
                  isActive = location.pathname === itemPath && !location.search;
                }
                const IconComponent = item.icon;
                const badgeCount =
                  item.path === '/approvals' ? pendingCounts.companyInvoiceCount
                  : item.path === '/department-approvals' ? pendingCounts.invoiceCount
                  : item.path === '/expense-approvals' ? (pendingCounts.companyExpenseCount || pendingCounts.expenseCount)
                  : item.path === '/exchange-rate-reconciliation' ? pendingCounts.rateReconciliationCount
                  : (item.path === '/vendors' && flyGroup.id === 'actionItems') ? pendingCounts.vendorApprovalCount
                  : item.path === '/invoice-posting' ? pendingCounts.postToFinanceCount
                  : item.path === '/support/tickets' ? pendingCounts.openTicketCount
                  : 0;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => { setSidebarOpen(false); setHoveredGroup(null); }}
                    className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800/60 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <IconComponent className="w-4 h-4 mr-2.5 flex-shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {badgeCount > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full bg-red-500 text-white">
                        {badgeCount > 9 ? '9+' : badgeCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Compact mode floating search panel – collapsed only */}
      {menuMode === 'compact' && !compactExpanded && compactSearchOpen && (() => {
        const searchTerm = menuSearch.toLowerCase().trim();
        const allResults = menuGroups.flatMap(group =>
          group.items.filter(shouldShowNavItem).map(i => ({ ...i, groupLabel: group.label }))
        ).filter(i => !searchTerm || i.label.toLowerCase().includes(searchTerm));
        return (
          <div
            className="fixed bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 z-[60] overflow-hidden"
            style={{ left: 56, top: 48, width: 272, maxHeight: 'calc(100vh - 64px)' }}
          >
            <div className="px-3 py-2.5 border-b border-gray-100 dark:border-slate-700/60 bg-gray-50 dark:bg-slate-800">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                  autoFocus
                  type="text"
                  value={menuSearch}
                  onChange={e => setMenuSearch(e.target.value)}
                  placeholder="Search all menu items…"
                  className="w-full pl-7 pr-3 py-1.5 text-xs bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-md text-gray-700 dark:text-slate-300 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-cyan-500"
                />
                {menuSearch && (
                  <button onClick={() => setMenuSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">×</button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
              {allResults.length === 0 ? (
                <p className="px-3 py-8 text-xs text-gray-400 dark:text-slate-500 text-center">
                  {menuSearch ? 'No matching items' : 'Start typing to search…'}
                </p>
              ) : allResults.map((item) => {
                const [itemPath, itemQuery] = item.path.split('?');
                const isActive = itemQuery
                  ? location.pathname === itemPath && location.search === `?${itemQuery}`
                  : location.pathname === itemPath && !location.search;
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => { setCompactSearchOpen(false); setMenuSearch(''); setSidebarOpen(false); }}
                    className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800/60 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <IconComponent className="w-4 h-4 mr-2.5 flex-shrink-0" />
                    <span className="flex-1 min-w-0 truncate">{item.label}</span>
                    <span className="ml-2 text-[10px] text-gray-400 dark:text-slate-500 shrink-0">{item.groupLabel}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Mobile voice FAB — only rendered on mobile; desktop uses the TopBar button */}
      <div className="lg:hidden">
        <VoiceAgentButton mobileOnly />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900 bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar with notifications and chat */}
        <TopBar onOpenChat={() => setChatOpen(true)} pendingCounts={pendingCounts} />

        {/* Top bar for mobile */}
        <header className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between h-14 px-4">
            {/* Hamburger */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Open menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Logo */}
            <div className="flex items-center">
              <SpocoDocLogo size={24} textClass="text-sm font-bold text-gray-900 dark:text-white" />
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-1">
              {/* Notification bell with badge */}
              <button
                onClick={() => navigate('/activity-feed')}
                className="relative p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Activity"
              >
                <Bell className="h-5 w-5" />
                {(() => {
                  const c = (pendingCounts?.invoiceCount || 0)
                    + (pendingCounts?.expenseCount || 0)
                    + (pendingCounts?.rateReconciliationCount || 0)
                    + (pendingCounts?.vendorApprovalCount || 0)
                    + (pendingCounts?.postToFinanceCount || 0)
                    + (pendingCounts?.companyInvoiceCount || 0)
                    + (pendingCounts?.companyExpenseCount || 0);
                  return c > 0 ? (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {c > 9 ? '9+' : c}
                    </span>
                  ) : null;
                })()}
              </button>

              {/* Chat */}
              <button
                onClick={() => setChatOpen(true)}
                className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Chat"
              >
                <MessageCircle className="h-5 w-5" />
              </button>

              {/* User avatar — opens profile dropdown */}
              <div className="relative ml-1" ref={mobileProfileRef}>
                <button
                  onClick={() => setShowMobileProfile(v => !v)}
                  className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <span className="text-white font-semibold text-xs">
                    {(() => {
                      const fn = user?.firstName || user?.FirstName || '';
                      const ln = user?.lastName  || user?.LastName  || '';
                      if (fn && ln) return (fn[0] + ln[0]).toUpperCase();
                      return (user?.email?.[0] || 'U').toUpperCase();
                    })()}
                  </span>
                </button>

                {showMobileProfile && (
                  <div className="absolute right-0 top-10 w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-sm">
                            {(() => {
                              const fn = user?.firstName || user?.FirstName || '';
                              const ln = user?.lastName  || user?.LastName  || '';
                              if (fn && ln) return (fn[0] + ln[0]).toUpperCase();
                              return (user?.email?.[0] || 'U').toUpperCase();
                            })()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white truncate">
                            {[user?.firstName || user?.FirstName, user?.lastName || user?.LastName].filter(Boolean).join(' ') || user?.email}
                          </p>
                          <p className="text-xs text-blue-100 truncate">{user?.email}</p>
                        </div>
                      </div>
                    </div>

                    {/* Info rows */}
                    <div className="divide-y divide-gray-100">
                      <div className="px-4 py-2.5 flex items-center gap-3">
                        <Shield className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Role</p>
                          <p className="text-sm text-gray-800 font-medium truncate">{user?.role || 'User'}</p>
                        </div>
                      </div>
                      <div className="px-4 py-2.5 flex items-center gap-3">
                        <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Company</p>
                          <p className="text-sm text-gray-800 font-medium truncate">{currentCompany?.CompanyName || '—'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="border-t border-gray-200 p-2">
                      <Link
                        to="/notification-preferences"
                        onClick={() => setShowMobileProfile(false)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <Bell className="h-4 w-4" />
                        Notification Settings
                      </Link>
                      <button
                        onClick={() => { setShowMobileProfile(false); handleLogout(); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
          <TrialBanner />
          <div className="w-full px-6 py-6">
            <Outlet />
          </div>
        </main>

        {/* Global upload progress indicator */}
        <UploadProgressIndicator />
      </div>

      {/* Chat modal */}
      <ChatModal open={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
}
